import { useQuery } from '@tanstack/react-query';
import type {
  LessonBlockContentPatch,
  LessonBlockCopyRequest,
  LessonBlockCopyResponse,
  LessonBlockCreateRequest,
  LessonBlockCreateResponse,
  LessonBlockMoveRequest,
  LessonBlockMutationResponse,
} from '@course-authoring/shared-types';
import { fetchWithAuthRetry, parseApiError } from './apiUtils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/** `{ [blockId]: externalAccountId[] }` — English overrides only, by currently-assigned account (NA-295). */
export type LessonBlockCustomizations = Record<string, number[]>;

export const lessonBlockCustomizationKeys = {
  all: ['lessonBlockCustomizations'] as const,
  detail: (lessonId: string) => [...lessonBlockCustomizationKeys.all, lessonId] as const,
};

export async function fetchLessonBlockCustomizations(
  lessonId: string,
): Promise<LessonBlockCustomizations> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/lessons/${lessonId}/block-customizations`);
  if (!response.ok) throw await parseApiError(response, 'Failed to fetch lesson block customizations');
  return response.json();
}

/** Fetched only in Standard/English context — see LessonEditorPage's `enabled` gate. */
export function useLessonBlockCustomizationsQuery(lessonId: string, enabled = true) {
  return useQuery({
    queryKey: lessonBlockCustomizationKeys.detail(lessonId),
    queryFn: () => fetchLessonBlockCustomizations(lessonId),
    enabled: enabled && Boolean(lessonId),
    staleTime: 0,
  });
}

function blockUrl(
  lessonId: string,
  suffix: string,
  accountId: number | null,
): string {
  const params = new URLSearchParams({ locale: 'en' });
  if (accountId !== null) params.set('accountId', String(accountId));
  return `${API_BASE_URL}/lessons/${lessonId}/blocks${suffix}?${params}`;
}

export async function patchLessonBlockContent(
  lessonId: string,
  blockId: string,
  patch: LessonBlockContentPatch,
  accountId: number | null = null,
): Promise<LessonBlockMutationResponse> {
  const response = await fetchWithAuthRetry(blockUrl(lessonId, `/${blockId}/content`, accountId), {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  if (!response.ok) throw await parseApiError(response, 'Failed to save lesson block content');
  return response.json();
}

export async function createLessonBlock(
  lessonId: string,
  request: LessonBlockCreateRequest,
  accountId: number | null = null,
): Promise<LessonBlockCreateResponse> {
  const response = await fetchWithAuthRetry(blockUrl(lessonId, '', accountId), {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (!response.ok) throw await parseApiError(response, 'Failed to create lesson block');
  return response.json();
}

/**
 * NA-437: copies a block into a different, existing Emtrain lesson.
 * Standard-context only (no accountId/locale query params — the endpoint
 * never operates in client-account context), so this doesn't go through
 * `blockUrl`'s account/locale plumbing like the other block mutations.
 */
export async function copyLessonBlockToLesson(
  destinationLessonId: string,
  request: LessonBlockCopyRequest,
): Promise<LessonBlockCopyResponse> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/lessons/${destinationLessonId}/blocks/copy`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (!response.ok) throw await parseApiError(response, 'Failed to copy lesson block');
  return response.json();
}

export async function moveLessonBlock(
  lessonId: string,
  blockId: string,
  request: LessonBlockMoveRequest,
  accountId: number | null = null,
): Promise<LessonBlockMutationResponse> {
  const response = await fetchWithAuthRetry(blockUrl(lessonId, `/${blockId}/position`, accountId), {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
  if (!response.ok) throw await parseApiError(response, 'Failed to move lesson block');
  return response.json();
}

export async function deleteLessonBlock(
  lessonId: string,
  blockId: string,
  accountId: number | null = null,
): Promise<LessonBlockMutationResponse> {
  const response = await fetchWithAuthRetry(blockUrl(lessonId, `/${blockId}`, accountId), {
    method: 'DELETE',
  });
  if (!response.ok) throw await parseApiError(response, 'Failed to delete lesson block');
  return response.json();
}
